/**
 * MemoriesSection — what agents already know about this installation, and what
 * they are asking to learn.
 *
 * A memory is a procedure that was worked out here once: which toolchains exist
 * and where, how to reach a device, why the obvious route fails. Its `hook` —
 * one line — travels in the system prompt of every thread that can see it, and
 * the body is fetched only when the case matches. That split is the whole
 * design, and it is why this panel puts the index's size on screen: the hooks
 * are paid on every turn of every thread forever, so their cost has to be
 * visible to whoever is deciding whether to add one more.
 *
 * ── Four tabs, two audiences ──────────────────────────────────────────────
 *
 * "Mías" is self-service: a person's own standing preferences, editable
 * without any permission, because asking an administrator to state your own
 * preference makes the scope pointless. The other three — general, per project,
 * and the proposal queue — are `m.view`/`m.edit`, and the tabs simply are not
 * offered without it. `memoryAdminRoutes.ts` enforces the same split, so the
 * hidden tabs are a courtesy rather than the protection.
 *
 * ── Why proposals are a queue and not a switch ────────────────────────────
 *
 * An agent that could write its own memories would turn one wrong conclusion
 * into a standing instruction applied to every future thread, with nobody in
 * the loop. So agents propose, a person approves, and until then the proposal
 * is inert — no index, no reader. The queue is where that decision happens, and
 * it shows the whole body, because approving a hook you have not read is the
 * same failure one step removed.
 *
 * Follows AgentsSection's pattern: manual `refresh()` + useState, ModalShell
 * for the editor, `window.confirm` before archiving.
 */
import type {
  AgentMemory,
  AgentMemoryId,
  ManagedAgent,
  ManagedAgentId,
  ProjectId,
  Tag,
  UserId,
} from "@taskin-code/contracts";
import { AGENT_MEMORY_HOOK_MAX_LENGTH } from "@taskin-code/contracts";
import { useEffect, useMemo, useState } from "react";

import { AgentsClient } from "../../agents/AgentsClient";
import { useAuthStore } from "../../auth/authStore";
import { MemoriesClient } from "../../memories/MemoriesClient";
import { TagsClient } from "../../tags/TagsClient";
import { useStore } from "../../store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  approximateTokens,
  buildMemoryCreateRequest,
  buildMemoryUpdateRequest,
  EMPTY_MEMORY_FORM,
  memoryToFormState,
  slugFromTitle,
  sortMemories,
  validateMemoryForm,
  type MemoryFormState,
} from "./MemoriesSection.logic";
import { Field, ModalShell } from "./settingsModal";
import { TagPicker } from "./TagPicker";

type MemoryTab = "general" | "project" | "user" | "proposals";

const TAB_LABELS: Record<MemoryTab, string> = {
  general: "Generales",
  project: "Por proyecto",
  user: "Mías",
  proposals: "Propuestas",
};

const STATUS_LABELS: Record<AgentMemory["status"], string> = {
  proposed: "Propuesta",
  approved: "Activa",
  archived: "Retirada",
};

const STATUS_CLASSES: Record<AgentMemory["status"], string> = {
  proposed: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  approved: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  archived: "bg-muted text-muted-foreground",
};

function StatusBadge({ status }: { readonly status: AgentMemory["status"] }) {
  return (
    <span
      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_CLASSES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

/**
 * The editor.
 *
 * Slug and scope are locked once a memory exists: both are identity — the slug
 * is the handle agents were told to use, the scope decides who it steers — so
 * changing either is an archive plus a new memory, not an edit. On a new memory
 * the slug is derived from the title but stays editable, because the derived
 * one is a guess and the author is the one who knows what the agent will look
 * for.
 */
function MemoryEditor(props: {
  readonly title: string;
  readonly initial: MemoryFormState;
  readonly locked: boolean;
  readonly availableTags: ReadonlyArray<Tag>;
  readonly onClose: () => void;
  readonly onSubmit: (form: MemoryFormState) => Promise<void>;
}) {
  const [form, setForm] = useState<MemoryFormState>(props.initial);
  const [slugTouched, setSlugTouched] = useState(props.locked);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const patch = (next: Partial<MemoryFormState>) => setForm((prev) => ({ ...prev, ...next }));

  const submit = async () => {
    const invalid = validateMemoryForm(form);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await props.onSubmit(form);
      props.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar la memoria.");
    } finally {
      setSaving(false);
    }
  };

  const hookLeft = AGENT_MEMORY_HOOK_MAX_LENGTH - form.hook.trim().length;

  return (
    <ModalShell title={props.title} wide onClose={props.onClose}>
      <div className="flex flex-col gap-4">
        <Field label="Título">
          <Input
            value={form.title}
            onChange={(e) => {
              const title = e.target.value;
              patch(slugTouched ? { title } : { title, slug: slugFromTitle(title) });
            }}
            placeholder="Cómo instalar un APK en el emulador"
          />
        </Field>

        <Field label="Slug">
          <Input
            value={form.slug}
            disabled={props.locked}
            onChange={(e) => {
              setSlugTouched(true);
              patch({ slug: e.target.value });
            }}
            placeholder="instalar-apk-en-emulador"
          />
          <p className="text-[11px] text-muted-foreground">
            {props.locked
              ? "El slug no se puede cambiar: es el identificador que los agentes ya conocen."
              : "Es lo que el agente escribe para leer la memoria. Minúsculas, números y guiones."}
          </p>
        </Field>

        <Field label="Gancho">
          <Input
            value={form.hook}
            onChange={(e) => patch({ hook: e.target.value })}
            placeholder="Dónde está el SDK de Android y cómo llegar al emulador"
          />
          <p
            className={`text-[11px] ${hookLeft < 0 ? "text-destructive" : "text-muted-foreground"}`}
          >
            La única línea que viaja en el prompt de cada turno. Di <em>qué</em> resuelve, no cómo.
            Quedan {hookLeft} caracteres.
          </p>
        </Field>

        <Field label="Cuándo aplica (opcional)">
          <Input
            value={form.triggerHint}
            onChange={(e) => patch({ triggerHint: e.target.value })}
            placeholder="Cuando alguien pide correr la app en un dispositivo"
          />
          <p className="text-[11px] text-muted-foreground">
            No lo ve el agente: es para quien revise esta memoria más adelante.
          </p>
        </Field>

        <Field label="Contenido">
          <Textarea
            value={form.body}
            onChange={(e) => patch({ body: e.target.value })}
            rows={12}
            className="font-mono text-xs"
            placeholder={"Comandos, rutas y por qué falló lo que se probó antes."}
          />
          <p className="text-[11px] text-muted-foreground">
            Esto sólo se paga cuando el agente lo pide, así que escríbelo completo: comandos
            exactos, rutas absolutas y qué camino <em>no</em> funciona.
          </p>
        </Field>

        <Field label="Etiquetas">
          <TagPicker
            available={props.availableTags}
            selected={form.tagIds}
            onChange={(tagIds) => patch({ tagIds })}
          />
        </Field>

        {error !== null && <p className="text-xs text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={props.onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function MemoryCard(props: {
  readonly memory: AgentMemory;
  readonly canEdit: boolean;
  readonly onEdit: () => void;
  readonly onArchive: () => void;
  readonly onApprove: () => void;
}) {
  const { memory } = props;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md border border-border/60 bg-card/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="truncate text-sm font-semibold">{memory.title}</h4>
            <StatusBadge status={memory.status} />
          </div>
          <code className="mt-0.5 block truncate text-[11px] text-muted-foreground">
            {memory.slug}
          </code>
          <p className="mt-1.5 text-xs text-muted-foreground">{memory.hook}</p>
          {memory.triggerHint !== null && (
            <p className="mt-1 text-[11px] italic text-muted-foreground">
              Aplica cuando: {memory.triggerHint}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {props.canEdit && memory.status !== "approved" && (
            <Button size="sm" onClick={props.onApprove}>
              {memory.status === "proposed" ? "Aprobar" : "Reactivar"}
            </Button>
          )}
          {props.canEdit && (
            <Button size="sm" variant="ghost" onClick={props.onEdit}>
              Editar
            </Button>
          )}
          {props.canEdit && memory.status !== "archived" && (
            <Button size="sm" variant="ghost" onClick={props.onArchive}>
              {/*
                Same call, different word: retiring something that is steering
                agents and throwing away something that never did are not the
                same decision, and the button should not pretend they are.
              */}
              {memory.status === "proposed" ? "Descartar" : "Retirar"}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
        {/*
          The read count is the only honest signal that separates a memory doing
          its job from one that has been riding in every system prompt for a
          month steering nothing.
        */}
        <span>
          {memory.readCount === 0
            ? "Nunca leída por un agente"
            : `Leída ${memory.readCount} ${memory.readCount === 1 ? "vez" : "veces"}`}
        </span>
        <button
          type="button"
          className="underline underline-offset-2 hover:text-foreground"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Ocultar contenido" : "Ver contenido"}
        </button>
      </div>

      {expanded && (
        <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-muted/40 p-3 font-mono text-[11px] leading-relaxed">
          {memory.body}
        </pre>
      )}
    </div>
  );
}

/**
 * What a thread would actually carry, in bytes and in approximate tokens.
 *
 * `dropped` matters more than it looks: when the index cap bites, the memories
 * that did not fit are invisible to the agent *and* to whoever wrote them, and
 * a truncated index reads exactly like a complete one. This is the only place
 * that says so.
 */
function IndexCost(props: {
  readonly bytes: number;
  readonly entries: number;
  readonly dropped: number;
  readonly scopeLabel: string;
}) {
  return (
    <div className="rounded-md border border-border/60 bg-card/40 px-4 py-3">
      <p className="text-xs">
        <span className="font-medium">{props.entries}</span> memorias viajan en el prompt de{" "}
        {props.scopeLabel}: <span className="font-medium">{props.bytes} bytes</span> (~
        {approximateTokens(props.bytes)} tokens) en cada turno.
      </p>
      {props.dropped > 0 && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          {props.dropped}{" "}
          {props.dropped === 1 ? "memoria no cabe en el índice" : "memorias no caben en el índice"}{" "}
          y ningún agente las ve. Retira alguna para hacerles sitio.
        </p>
      )}
    </div>
  );
}

export function MemoriesSection() {
  const hasView = useAuthStore((state) => state.globalPermissions.has("m.view"));
  const hasEdit = useAuthStore((state) => state.globalPermissions.has("m.edit"));
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const projects = useStore((state) => state.projects);

  const tabs = useMemo<ReadonlyArray<MemoryTab>>(
    () => (hasView ? (["general", "project", "user", "proposals"] as const) : (["user"] as const)),
    [hasView],
  );
  const [tab, setTab] = useState<MemoryTab>(hasView ? "general" : "user");
  const [projectId, setProjectId] = useState<ProjectId | null>(
    (projects[0]?.id as ProjectId | undefined) ?? null,
  );
  const [memories, setMemories] = useState<ReadonlyArray<AgentMemory>>([]);
  /**
   * The vocabulary, loaded once for the editor's picker.
   *
   * Failures are swallowed on purpose: a person without `t.view` can still
   * write memories, they just cannot tag them — and an error banner about a
   * screen they were never promised would only be noise.
   */
  const [availableTags, setAvailableTags] = useState<ReadonlyArray<Tag>>([]);
  /**
   * The agents offered in the cost panel's "as this agent" selector.
   *
   * Without one the panel answers "what exists in this scope?", which stopped
   * being the useful question the moment tags could narrow it. Loaded
   * best-effort: an `m.view` holder without `a.view` still gets the unfiltered
   * number, which is what they had before.
   */
  const [agents, setAgents] = useState<ReadonlyArray<ManagedAgent>>([]);
  const [previewAgentId, setPreviewAgentId] = useState<ManagedAgentId | null>(null);
  const [preview, setPreview] = useState<{
    readonly bytes: number;
    readonly entries: number;
    readonly dropped: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AgentMemory | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const listed =
        tab === "proposals"
          ? await MemoriesClient.listProposals()
          : await MemoriesClient.list({
              scope: tab,
              projectId: tab === "project" ? projectId : null,
              // Absent means "mine", which is what makes this tab reachable
              // without `m.view`.
              userId: null,
            });
      setMemories(listed);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudieron cargar las memorias.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, projectId]);

  useEffect(() => {
    void TagsClient.list()
      .then((entries) => setAvailableTags(entries.map((entry) => entry.tag)))
      .catch(() => setAvailableTags([]));
    void AgentsClient.list()
      .then(setAgents)
      .catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    if (!hasView) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const previewAgent = agents.find((candidate) => candidate.id === previewAgentId) ?? null;
    void MemoriesClient.indexPreview({
      projectId: tab === "project" ? projectId : null,
      userId: currentUserId,
      agentId: previewAgent?.id ?? null,
      strict: previewAgent?.memoryStrict ?? false,
    })
      .then((result) => {
        if (cancelled) return;
        setPreview({
          bytes: result.bytes,
          entries: result.entries.length,
          dropped: result.dropped,
        });
      })
      // A failed preview is a missing number, not a broken panel — the lists
      // above it are what the person came for.
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [hasView, tab, projectId, currentUserId, memories, previewAgentId, agents]);

  const canEditCurrentTab = tab === "user" ? true : hasEdit;

  const create = async (form: MemoryFormState) => {
    await MemoriesClient.create(
      buildMemoryCreateRequest(form, {
        projectId: form.scope === "project" ? projectId : null,
        userId: form.scope === "user" ? (currentUserId as UserId | null) : null,
      }),
    );
    await refresh();
  };

  const update = async (form: MemoryFormState) => {
    if (editing === null) return;
    await MemoriesClient.update(editing.id, buildMemoryUpdateRequest(form, editing));
    await refresh();
  };

  const archive = async (memoryId: AgentMemoryId) => {
    const isProposal = memories.find((m) => m.id === memoryId)?.status === "proposed";
    const question = isProposal
      ? "¿Descartar esta propuesta? El agente no volverá a proponerla en este hilo."
      : "¿Retirar esta memoria? Dejará de guiar a los agentes.";
    if (!window.confirm(question)) return;
    try {
      await MemoriesClient.archive(memoryId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo retirar la memoria.");
    }
  };

  const approve = async (memoryId: AgentMemoryId) => {
    try {
      await MemoriesClient.approve(memoryId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo aprobar la memoria.");
    }
  };

  const sorted = sortMemories(memories);
  const scopeLabel =
    tab === "project"
      ? "un hilo de este proyecto"
      : tab === "user"
        ? "tus hilos"
        : "cualquier hilo";

  return (
    <div className="flex flex-col gap-5">
      <div className="min-w-0">
        <h3 className="text-base font-semibold">Memoria</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Los procedimientos que ya se resolvieron aquí, para que el siguiente agente no los vuelva
          a improvisar. Cada memoria aporta una línea al prompt de los hilos que la alcanzan; el
          contenido completo sólo se lee cuando el caso encaja.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {tabs.map((candidate) => (
          <button
            key={candidate}
            type="button"
            onClick={() => setTab(candidate)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              tab === candidate
                ? "bg-primary text-primary-foreground"
                : "bg-muted/40 text-muted-foreground hover:bg-muted/70"
            }`}
          >
            {TAB_LABELS[candidate]}
          </button>
        ))}
      </div>

      {tab === "project" && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-muted-foreground" htmlFor="memory-project">
            Proyecto
          </label>
          <select
            id="memory-project"
            className="rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs"
            value={projectId ?? ""}
            onChange={(e) => setProjectId((e.target.value || null) as ProjectId | null)}
          >
            {projects.length === 0 && <option value="">No hay proyectos</option>}
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {tab !== "proposals" && agents.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-muted-foreground" htmlFor="memory-preview-agent">
            Ver como
          </label>
          <select
            id="memory-preview-agent"
            className="rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs"
            value={previewAgentId ?? ""}
            onChange={(e) => setPreviewAgentId((e.target.value || null) as ManagedAgentId | null)}
          >
            <option value="">Sin filtrar por agente</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
                {agent.memoryStrict ? " · sólo etiquetado" : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {preview !== null && tab !== "proposals" && (
        <IndexCost
          bytes={preview.bytes}
          entries={preview.entries}
          dropped={preview.dropped}
          scopeLabel={scopeLabel}
        />
      )}

      {tab === "proposals" && (
        <p className="text-xs text-muted-foreground">
          Lo que los agentes aprendieron y piden guardar. Mientras estén aquí no guían nada: no
          aparecen en ningún índice y ningún agente puede leerlas. Lee el contenido antes de aprobar
          — una memoria equivocada se convierte en una instrucción permanente.
        </p>
      )}

      {error !== null && <p className="text-xs text-destructive">{error}</p>}

      {loading ? (
        <p className="text-xs text-muted-foreground">Cargando…</p>
      ) : sorted.length === 0 ? (
        <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
          {tab === "proposals"
            ? "Ningún agente ha propuesto nada todavía."
            : "Todavía no hay memorias en este ámbito."}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sorted.map((memory) => (
            <MemoryCard
              key={memory.id}
              memory={memory}
              canEdit={canEditCurrentTab}
              onEdit={() => setEditing(memory)}
              onArchive={() => void archive(memory.id)}
              onApprove={() => void approve(memory.id)}
            />
          ))}
        </div>
      )}

      {tab !== "proposals" && canEditCurrentTab && (
        <div>
          <Button
            variant="outline"
            onClick={() => setCreating(true)}
            disabled={tab === "project" && projectId === null}
          >
            Nueva memoria
          </Button>
        </div>
      )}

      {creating && (
        <MemoryEditor
          title="Nueva memoria"
          initial={{ ...EMPTY_MEMORY_FORM, scope: tab === "proposals" ? "general" : tab }}
          locked={false}
          availableTags={availableTags}
          onClose={() => setCreating(false)}
          onSubmit={create}
        />
      )}

      {editing !== null && (
        <MemoryEditor
          title={`Editar “${editing.title}”`}
          initial={memoryToFormState(editing)}
          locked
          availableTags={availableTags}
          onClose={() => setEditing(null)}
          onSubmit={update}
        />
      )}
    </div>
  );
}
